import { animate, state, style, transition, trigger } from '@angular/animations';
import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { Select, Store } from '@ngxs/store';
import { TariffFinderComponent } from '@shared-components/tariff-finder/tariff-finder.component';
import {
  FtaAdvisoryDetails,
  FtaAdvisoryDetailsBasedOnId,
} from '@shared-interfaces/fta-advisory';
import { FormDrawerService } from '@shared-services/form-drawer.service';
import {
  HttpErrorHandlerService,
  MessageType,
} from '@shared-services/http-error-handling.service';
import { BaseApp } from '@subscription-destroyer';
import { Observable } from 'rxjs';
import { BaseSharedState } from 'src/app/shared-resources/store/base.state';
import { FtaAdvisory } from '../services/fta-advisory.service';
import { CommonConstants } from 'src/app/shared-resources/constants/common.constants';

@Component({
  selector: 'app-fta-advisor',
  templateUrl: './fta-advisor.component.html',
  styleUrls: ['./fta-advisor.component.less'],
  providers: [BaseApp],
  animations: [
    trigger('fadeInOut', [
      state('void', style({ opacity: 0 })),
      state('*', style({ opacity: 1 })),
      transition('void => *', [animate('0.5s 0.5s ease-in')]),
      transition('* => void', [animate('0.5s ease-in')])
    ])
 ]
})
export class FtaAdvisorComponent implements OnInit {
  public CommonConstants = CommonConstants;
  public calculatingFtaAdvisory: boolean;
  public ftaAdvisorForm: FormGroup;
  public tariffFinderFlag: string;
  public productAdvisoryType = false;
  public pageSize = 10;
  public pageIndex = 1;
  public searchTablePageIndex = 1;
  public totalElements = 0;
  public totalFTADetailsElements = 0;
  public isVisible = false;
  public showFTANotesDescription = '';
  public searchHSCode = '';
  public searchDescription = '';
  public tariffCodeId: number;
  public FTATitle = '';
  public disable = false;
  public originCountrySelected = false;
  public selectedCountry: string;
  public originCountryCode = '';
  public loadingFtaDetails = false;
  public shake: boolean = false;

  public viewModel: {
    tariffCode?: string;
    hsCodeList: FtaAdvisoryDetails[];
  } = {
      tariffCode: '',
      hsCodeList: [],
    };

  public viewModelForFTADetailsById: {
    tariffCode?: number;
    ftaDetailsList: FtaAdvisoryDetailsBasedOnId[];
  } = {
      tariffCode: undefined,
      ftaDetailsList: [],
    };

  public countryList: any = [];
  public innerTableMap = new Map();

  //@Select(BaseSharedState.getRdmData) rdmList$: Observable<any>;
  rdmList$: Observable<any>;

  constructor(
    private fb: FormBuilder,
    private formDrawerService: FormDrawerService,
    private translate: TranslateService,
    private subscribtions: BaseApp,
    private httpMessage: HttpErrorHandlerService,
    private ftaAdvisory: FtaAdvisory,
    private cdr: ChangeDetectorRef,
    private store: Store
  ) {
    this.rdmList$ = this.store.select(BaseSharedState.getRdmData);
    this.tariffFinderFlag = this.translate.instant(
      'ics-declaration.menu.tariff'
    );
  }

  ngOnInit() {
    this.initilizeFtaAdvisoryForm();
  }

  ngAfterViewChecked() {
    this.disable = this.ftaAdvisorForm.controls.destinationCountryIso.errors
      ? true
      : false;
    this.originCountrySelected = this.ftaAdvisorForm.controls.originCountryIso
      .value
      ? true
      : false;
    this.cdr.detectChanges();
  }

  initilizeFtaAdvisoryForm(): void {
    this.ftaAdvisorForm = this.fb.group({
      originCountryIso: [null],
      destinationCountryIso: [null, [Validators.required]],
      productBased: [null, [Validators.required]],
      hsCode: [null],
    });

    const productType$ = this.ftaAdvisorForm.controls.productBased.valueChanges;
    this.subscribtions.subs.add(
      productType$.subscribe((res) => {
        this.productAdvisoryType = res === 'true' ? true : false;
        if (res === 'false') {
          this.ftaAdvisorForm.controls.hsCode.setValidators(null);
          this.ftaAdvisorForm.patchValue({
            hsCode: null,
          });
        } else {
          this.ftaAdvisorForm.controls.hsCode.setValidators([
            Validators.required,
          ]);
        }
      })
    );
  }

  tariffFinder(index: any): void {
    const customInfoTariff = {
      hsCode: index,
      destination: this.ftaAdvisorForm.value.destinationCountryIso,
    };

    this.subscribtions.subs.add(
      this.formDrawerService
        .openDrawerForm(
          TariffFinderComponent,
          'HS Code Finder',
          customInfoTariff
        )
        .subscribe((result: any) => {
          if (result) {
            let tariffCode = null;
            if (result.selectedTariffs && Array.isArray(result.selectedTariffs) && result.selectedTariffs.length > 0) {
              // Handle multiple tariff selection - take the first one
              tariffCode = result.selectedTariffs[0].tariffCode;
            } else if (result.tariffCode) {
              // Handle single tariff selection
              tariffCode = result.tariffCode;
            }
            if (tariffCode) {
              this.ftaAdvisorForm.controls['hsCode'].setValue(tariffCode);
            }
          }
        })
    );
  }

  searchFtaAdvisory(pageNumber?: number): void {
    this.calculatingFtaAdvisory = true;
    if (!this.ftaAdvisorForm.valid) {
      this.calculatingFtaAdvisory = false;
      return;
    }
    if (pageNumber) {
      this.searchTablePageIndex = pageNumber;
    }

    const ftaAdvisoryResponse$ = this.ftaAdvisory.searchFTAAdvisory(
      this.ftaAdvisorForm.value,
      this.pageSize,
      this.searchTablePageIndex
    );
    this.subscribtions.subs.add(
      ftaAdvisoryResponse$.subscribe(
        (res: any) => {
          if (res && res.ftaSearchResponseDto) {
            this.calculatingFtaAdvisory = false;
            this.viewModel.hsCodeList = res.ftaSearchResponseDto['content'];
            this.totalElements = res.ftaSearchResponseDto['totalElements'];
          }
        },
        (error: any) => {
          this.calculatingFtaAdvisory = false;
          this.httpMessage.showTopFloatMessage(
            error.error,
            MessageType.ERROR,
            this.translate.instant('app.forgotpassword.request.failed')
          );
        }
      )
    );
  }

  showFTADetails(
    tariffCodeId: number,
    title: string,
    pageIndex: number,
    hscode?: string,
    hsDesc?: string
  ): void {
    this.FTATitle = this.translate.instant('app.label.ftaDetails') + ' - ' + title;
    this.loadingFtaDetails = true;
    this.tariffCodeId = tariffCodeId;
    this.pageIndex = pageIndex;
    const ftaDetailsBasedOnId$ = this.ftaAdvisory.getFTADetailsbyTariffCode(
      tariffCodeId,
      this.ftaAdvisorForm.value,
      this.pageSize,
      this.pageIndex,
      hscode,
      hsDesc
    );
    const countryList$ = this.ftaAdvisory.getCountryList(
      tariffCodeId,
      this.ftaAdvisorForm.controls.destinationCountryIso.value
    );
    this.subscribtions.subs.add(
      ftaDetailsBasedOnId$.subscribe(
        (res: any) => {
          if (res && res.ftaHsCodeSearchResponses) {
            this.viewModelForFTADetailsById.ftaDetailsList = res.ftaHsCodeSearchResponses['content'];
            this.viewModelForFTADetailsById.ftaDetailsList.forEach(
              (res, index) => {
                res['expand'] = false;
                res['index'] = index;
              }
            );
            this.totalFTADetailsElements = res.ftaHsCodeSearchResponses['totalElements'];
            this.innerTableMap.clear();
          }
          this.loadingFtaDetails = false;
        },
        (error: any) => {
          console.log(error);
          this.viewModelForFTADetailsById.ftaDetailsList = [];
          this.totalFTADetailsElements = 0;
          this.httpMessage.showTopFloatMessage(
            error.error,
            MessageType.ERROR,
            this.translate.instant('app.forgotpassword.request.failed')
          );
          this.loadingFtaDetails = false;
        }
      ),
      countryList$.subscribe(
        (res: any) => {
          if (res) {
            this.countryList = res;
            this.countryList.length == 1
              ? ((this.selectedCountry = this.countryList[0].iso),
              (this.originCountrySelected = true),
              (this.originCountryCode = this.selectedCountry))
              : (this.selectedCountry = '');
          }
        },
        (error: any) => {
          this.countryList = [];
          this.httpMessage.showTopFloatMessage(
            error.error,
            MessageType.ERROR,
            this.translate.instant('app.forgotpassword.request.failed')
          );
          this.loadingFtaDetails = false;
        }
      )
    );
    this.isVisible = true;
  }

  handleCancel(): void {
    this.isVisible = false;
    this.searchDescription = '';
    this.searchHSCode = '';
    this.showFTANotesDescription = '';
    this.originCountryCode = '';
    this.pageIndex = 1;
  }

  changePagination(
    pageNumber: number,
    searchHSCode: string,
    searchDescription: string
  ): void {
    this.loadingFtaDetails = true;
    this.showFTADetails(
      this.viewModelForFTADetailsById.ftaDetailsList[0].tariffCode,
      '',
      pageNumber,
      searchHSCode,
      searchDescription
    );
  }

  showNotesDetails(hscode: string): void {
    const data = this.viewModelForFTADetailsById.ftaDetailsList.filter(
      (x) => x.hscode === hscode
    );
    this.showFTANotesDescription = data[0].hscode;
  }

  shakeText(){
    this.shake = true;
    setTimeout(() => {
      this.shake = false;
    }, 2000);
  }

  reset(): void {
    this.searchHSCode = '';
    this.searchDescription = '';
    this.search('', '');
    this.innerTableMap.clear();
  }

  resetButton(): void {
    this.ftaAdvisorForm.reset();
  }

  search(searchHSCode: string, searchDescription: string): void {
    this.showFTADetails(
      this.tariffCodeId,
      '',
      1,
      searchHSCode,
      searchDescription
    );
  }

  getSelectedCountry(
    originCountryCode: any,
    hsCode: string,
    tariffCodeId: number,
    index: number
  ): void {
    this.loadingFtaDetails = true;
    const tariffData$ = this.ftaAdvisory.getTariffDetails(
      tariffCodeId,
      originCountryCode,
      this.ftaAdvisorForm.controls.destinationCountryIso.value,
      hsCode
    );
    this.subscribtions.subs.add(
      tariffData$.subscribe(
        (res: any) => {
          if (res) {
            this.innerTableMap.set(index, res);
          }
          this.loadingFtaDetails = false;
        },
        (error: any) => {
          this.innerTableMap.clear();
          this.httpMessage.showTopFloatMessage(
            error.error,
            MessageType.ERROR,
            this.translate.instant('app.forgotpassword.request.failed')
          );
          this.loadingFtaDetails = false;
        }
      )
    );
  }

  getSelectedCountryCode(event: string): void {
    this.originCountryCode = event;
    this.viewModelForFTADetailsById.ftaDetailsList.forEach((res) => {
      res['expand'] = false;
    });
    this.innerTableMap.clear();
  }

  showCountryDetails(
    hsCode: string,
    expand: boolean,
    tariffCodeId?: any,
    index?: any
  ): void {
    if (this.originCountrySelected && expand) {
      this.getSelectedCountry(
        this.ftaAdvisorForm.controls.originCountryIso.value,
        hsCode,
        tariffCodeId,
        index
      );
    }
    if (this.originCountryCode && expand) {
      this.getSelectedCountry(
        this.originCountryCode,
        hsCode,
        tariffCodeId,
        index
      );
    }
  }
}
